<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card dark outlined>
          <v-card-title class="secondary py-1 text-subtitle-1">
            <v-icon left dense> mdi-cogs </v-icon>
            Layouts
            <v-spacer />
          </v-card-title>
          <v-card-text class="pa-0">
            <v-overlay :value="loading" absolute>
              <v-progress-circular
                indeterminate
                size="64"
                color="primary"
              ></v-progress-circular>
            </v-overlay>
            <v-data-table
              :headers="tableHeaders"
              :items="selectedLayouts"
              sort-by="LayoutId"
              single-select
              return-object
              class="elevation-5"
            >
              <template v-slot:item.Stalls="{ item }">
                <span
                  v-for="(stallName, index) in getActiveStallNames(item)"
                  :key="index"
                >
                  {{ stallName }}<br />
                </span>
              </template>

              <template v-if="canManageLayouts" v-slot:[`item.actions`]="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      small
                      class="mr-2"
                      v-bind="attrs"
                      v-on="on"
                      @click="copyItem(item)"
                    >
                      mdi-content-copy
                    </v-icon>
                  </template>
                  <span>Copy on this stallholder</span>
                </v-tooltip>
                <template v-if="IsAdmin">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        class="mr-2"
                        v-bind="attrs"
                        v-on="on"
                        @click="copyToStallholder(item)"
                      >
                        mdi-file-replace-outline
                      </v-icon>
                    </template>
                    <span>Copy to another stallholder</span>
                  </v-tooltip>
                </template>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      small
                      class="mr-2"
                      v-bind="attrs"
                      v-on="on"
                      @click="editItem(item)"
                    >
                      mdi-pencil
                    </v-icon>
                  </template>
                  <span>Edit</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small v-bind="attrs" v-on="on" @click="deleteItem(item)">
                      mdi-delete
                    </v-icon>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </template>

              <!-- Stallholder menu -->
              <template v-slot:top>
                <v-toolbar flat>
                  <v-row>
                    <v-col cols="4">
                      <v-autocomplete
                        :items="visibleStallholders"
                        v-model="selectedStallHolder"
                        item-text="StallholderName"
                        return-object
                        filled
                        dense
                        outlined
                        hide-details
                        label="Stallholders"
                        @change="handleChangeStallHolder"
                      >
                        <template v-slot:no-data>
                          <v-list-item>
                            <v-list-item-title>
                              <v-icon small color="red lighten-1">
                                mdi-information
                              </v-icon>
                              No event found
                            </v-list-item-title>
                          </v-list-item>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="2"> </v-col>
                  </v-row>

                  <v-row class="mt-2 mb-2">
                    <v-col cols="12"></v-col>
                  </v-row>
                  <v-dialog v-model="dialog" max-width="500px" dark>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-if="canManageLayouts"
                        x-small
                        dark
                        class="mb-2"
                        @click="addLayout()"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon left x-small> mdi-plus </v-icon>
                        Add Layout
                      </v-btn>
                    </template>
                  </v-dialog>

                  <v-dialog v-model="dialogDeleteItem" max-width="520px">
                    <v-card>
                      <!-- #EBC09A -->
                      <v-card-title class="text-h6 text-wrap justify-center"
                        >Are you sure you want to delete this
                        layout?</v-card-title
                      >
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="closeDelete">Cancel</v-btn>
                        <v-btn
                          color="red darken-1"
                          text
                          @click="deleteItemConfirmed"
                          >OK</v-btn
                        >
                        <v-spacer></v-spacer>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>

                  <CopyToStallholderDialog
                    ref="copyToDialog"
                    v-model="copyToDialog"
                    mode="layout"
                    submit-label="Next"
                    :source-event-id="
                      (selectedEvent && selectedEvent.EventId) || ''
                    "
                    :source-stallholder-id="
                      (itemToCopy && itemToCopy.StallholderId) ||
                      (selectedStallHolder &&
                        selectedStallHolder.StallholderId) ||
                      ''
                    "
                    @copy="handleCopyToDestination"
                  />

                  <LayoutCopyMatchDialog
                    v-model="matchDialog"
                    :result="copyPreview"
                    :saving="copySaving"
                    @save="saveLayoutCopy"
                  />

                  <v-dialog v-model="dialogCopyItem" max-width="500px" dark>
                    <v-card>
                      <v-card-title class="text-h6"> Copy Layout </v-card-title>
                      <v-card-text>
                        <v-text-field
                          v-model="copyLayoutName"
                          label="Layout Name"
                          outlined
                          dense
                          :rules="[(v) => !!v || 'Layout name is required']"
                        ></v-text-field>
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="closeCopy">Cancel</v-btn>
                        <v-btn
                          color="primary"
                          text
                          @click="saveCopy"
                          :disabled="!copyLayoutName"
                          >Save</v-btn
                        >
                        <v-spacer></v-spacer>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </v-toolbar>
              </template>

              <template v-slot:no-data>
                <v-btn color="primary"> No data available </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import "izitoast/dist/css/iziToast.min.css";
import iziToast from "izitoast/dist/js/iziToast.min.js";
import { mapState, mapActions, mapMutations } from "vuex";
import { hasReadOrWrite, hasWrite } from "@/utils/permissions";
import CopyToStallholderDialog from "@/components/productManagement/CopyToStallholderDialog.vue";
import LayoutCopyMatchDialog from "@/components/layouts/LayoutCopyMatchDialog.vue";

export default {
  components: {
    CopyToStallholderDialog,
    LayoutCopyMatchDialog,
  },
  props: {
    productsData: {
      type: Object,
      default: () => ({
        layouts: [],
      }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    dialog: false,
    dialogDeleteItem: false,
    dialogDeleteAllItems: false,
    dialogCopyItem: false,
    copyToDialog: false,
    matchDialog: false,
    copyPreview: {},
    copySaving: false,
    copyDestination: null,

    headers: [
      { text: "Layout Name", value: "LayoutName", width: "40%" },
      // {
      //   text: "Stallholder Name",
      //   value: "StallholderName",
      //   align: "left",
      //   width: "30%",
      // },
      { text: "Stalls", value: "Stalls", align: "left", width: "40%" },
      {
        text: "Actions",
        value: "actions",
        align: "right",
        width: "20%",
        sortable: false,
      },
    ],

    deleteItemId: "",
    copyLayoutName: "",
    itemToCopy: null,

    editedIndex: -1,
    editedItem: {
      StallholderId: "",
      LayoutId: "",
    },
    defaultItem: {
      StallholderId: "",
      StallId: "",
      TerminalUserId: "",
      TerminalId: "",
      LayoutId: "",
    },

    valid: true,
  }),

  computed: {
    ...mapState("filter", ["globalEventFilter", "IsAdmin"]),
    ...mapState("admin", [
      "processedLayout",
      "currentStallHolders",
      "currentStallholder",
      "currentStalls",
    ]),

    selectedLayouts() {
      return this.productsData.layouts || [];
    },

    visibleStallholders() {
      const list = this.currentStallHolders || [];
      if (this.IsAdmin || hasWrite(this.globalEventFilter?.Permission) || hasReadOrWrite(this.globalEventFilter?.Permission)) {
        return list;
      }
      return list.filter((sh) => hasReadOrWrite(sh.Permission) || hasWrite(sh.Permission));
    },

    canManageLayouts() {
      return (
        this.IsAdmin ||
        hasWrite(this.globalEventFilter?.Permission) ||
        hasWrite(this.currentStallholder?.Permission) ||
        hasWrite(this.selectedStallHolder?.Permission)
      );
    },

    tableHeaders() {
      if (this.canManageLayouts) return this.headers;
      return this.headers.filter((h) => h.value !== "actions");
    },

    formTitle() {
      return this.editedIndex === -1 ? "New Layout" : "Edit Layout";
    },

    selectedEvent: {
      get() {
        return this.globalEventFilter;
      },
      set(value) {
        console.log("EVENT IS CHANGED MAKE API REQUEST", value);
      },
    },

    selectedStallHolder: {
      get() {
        return this.currentStallholder;
      },
      set(value) {
        this.setCurrentStallholder(value);
      },
    },

    selectStallHolder: {
      get() {
        return this.currentStallholder;
      },
      set(value) {
        this.selectedStallholder = value;
      },
    },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDeleteItem(val) {
      val || this.closeDelete();
    },
    dialogCopyItem(val) {
      val || this.closeCopy();
    },
  },

  methods: {
    ...mapActions("admin", ["postLayouts", "layoutCopy"]),
    ...mapMutations("admin", ["setCurrentLayout", "setCurrentStallholder"]),

    handleChangeStallHolder: function (obj) {
      if (obj) {
        this.setCurrentStallholder(obj);
        // Note: Data will be refreshed by parent component
      }
    },

    addLayout: function () {
      // Set current layout and stallholder in store
      this.setCurrentLayout({});
      this.setCurrentStallholder(this.selectedStallHolder);

      this.$router.push({
        name: "Layout Configuration",
        params: {
          id: "create",
          layoutData: {},
          stallholderData: this.selectedStallHolder,
        },
      });
    },

    editItem(item) {
      console.log("editItem item: ", item);

      // Set current layout and stallholder in store
      this.setCurrentLayout(item);
      this.setCurrentStallholder(this.selectedStallHolder);

      this.$router.push({
        name: "Layout Configuration",
        params: {
          id: "update",
          layoutData: item,
          stallholderData: this.selectedStallHolder,
        },
      });
    },

    deleteItem(item) {
      console.log("deleteItem (item)", item);
      this.deleteItemId = item.LayoutId;
      this.dialogDeleteItem = true;
    },

    deleteItemConfirmed(item) {
      console.log("deleteItemConfirmed (item)", item);
      console.log("deleteItemConfirmed (item)", this.deleteItemId);

      try {
        if (this.deleteItemId) {
          let deletedItem = {
            Type: "Layout",
            Action: "delete",
            EventId: JSON.stringify(this.selectedEvent.EventId),
            LayoutId: this.deleteItemId,
          };

          console.log("DELETE: ", deletedItem);

          this.postLayouts(deletedItem)
            .then((response) => {
              console.log("deleteItemConfirmed: ", response);

              iziToast.success({
                title: "Layout",
                message: "Successfully deleted the Layout!",
                position: "topRight",
              });

              this.$emit("refresh-data");
            })
            .catch((err) => {
              console.log("ERROR: ", err);

              iziToast.warning({
                title: "Layout",
                message: "Failed to delete record!",
                position: "center",
              });
            });
        }
      } catch (e) {
        console.log(e);
      }

      this.closeDelete();
    },

    close() {
      this.dialog = false;

      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    closeDelete() {
      this.dialogDeleteItem = false;

      this.deleteItemId = "";
    },

    copyItem(item) {
      // console.log('copyItem item: ', item)
      this.itemToCopy = item;
      this.copyLayoutName = item.LayoutName + " - Copy";
      this.dialogCopyItem = true;
    },

    copyToStallholder(item) {
      this.itemToCopy = item;
      this.copyToDialog = true;
    },

    async handleCopyToDestination(destination) {
      if (!this.itemToCopy || !destination) return;
      this.copyDestination = destination;
      if (this.$refs.copyToDialog) this.$refs.copyToDialog.setCopying(true);
      try {
        const response = await this.layoutCopy({
          event_items: [
            {
              Type: "layout_copy",
              Action: "Validate",
              Properties: {
                SourceLayoutId: this.itemToCopy.LayoutId,
                TargetEventId: destination.event.EventId,
                TargetStallholderId: destination.stallholder.StallholderId,
              },
            },
          ],
        });
        this.copyPreview = response || {};
        this.copyToDialog = false;
        this.matchDialog = true;
      } catch (err) {
        const message =
          err?.response?.data?.Message ||
          err?.message ||
          "Unable to match layout products.";
        if (this.$refs.copyToDialog) {
          this.$refs.copyToDialog.setError(message);
        } else {
          iziToast.warning({
            title: "Copy layout",
            message,
            position: "topRight",
          });
        }
      }
    },

    async saveLayoutCopy({ remaps, removeProductIds }) {
      if (!this.itemToCopy || !this.copyDestination) return;
      this.copySaving = true;
      try {
        await this.layoutCopy({
          event_items: [
            {
              Type: "layout_copy",
              Action: "Save",
              Properties: {
                SourceLayoutId: this.itemToCopy.LayoutId,
                TargetEventId: this.copyDestination.event.EventId,
                TargetStallholderId:
                  this.copyDestination.stallholder.StallholderId,
                Remaps: remaps,
                RemoveProductIds: removeProductIds,
              },
            },
          ],
        });
        iziToast.success({
          title: "Copy layout",
          message: `Layout copied to ${this.copyDestination.stallholder.StallholderName}.`,
          position: "topRight",
        });
        this.matchDialog = false;
        this.copyPreview = {};
        this.copyDestination = null;
        this.itemToCopy = null;
        this.$emit("refresh-data");
      } catch (err) {
        iziToast.warning({
          title: "Copy layout",
          message:
            err?.response?.data?.Message ||
            err?.message ||
            "Failed to save the copied layout.",
          position: "topRight",
        });
      } finally {
        this.copySaving = false;
      }
    },

    saveCopy() {
      console.log("Original item to copy: ", this.itemToCopy);
      const itemToCopy = {
        Type: "Layout",
        Action: "create",
        StallholderId: this.itemToCopy.StallholderId,
        EventId: this.itemToCopy.EventId,
        LayoutName: this.copyLayoutName,
        // Copied layouts must not inherit stall assignments (would steal them)
        Stalls: [],
        Layout: { Objects: this.itemToCopy.Objects },
      };

      // let eventSubmitItem =
      // {
      //     Type: "Layout",
      //     Action: "create",
      //     StallholderId: setStallholder.StallholderId,
      //     EventId: setEvent,
      //     LayoutName: "test",
      //     Stalls: [{StallId: 1, StallName: "test"}],
      //     Layout: {Objects:[{GroupName: "test", itemType: "Group", GroupType: "Standard", textColour: [], backgroundColour: []}]}
      // }
      // console.log('New layout name: ', itemToCopy)
      this.postLayouts(itemToCopy)
        .then((response) => {
          console.log("saveCopy response: ", response);

          iziToast.success({
            title: "Layout",
            message: "Successfully inserted record!",
            position: "topRight",
          });

          this.$emit("refresh-data");
        })
        .catch((err) => {
          console.log("ERROR: ", err);
        });

      this.closeCopy();
    },

    closeCopy() {
      this.dialogCopyItem = false;
      this.copyLayoutName = "";
      this.itemToCopy = null;
    },

    getActiveStallNames(item) {
      // Prefer stalls from the layouts API (authoritative). Event metadata
      // strips LayoutId from stalls, so currentStalls cannot drive this column.
      if (item.Stalls && item.Stalls.length > 0) {
        return item.Stalls.map(
          (stall) => stall.StallName || stall.StallId
        ).filter(Boolean);
      }
      if (
        item.LayoutId &&
        this.currentStalls &&
        this.currentStalls.length > 0
      ) {
        return this.currentStalls
          .filter(
            (stall) =>
              stall.LayoutId != null &&
              String(stall.LayoutId) === String(item.LayoutId)
          )
          .map((stall) => stall.StallName)
          .filter(Boolean);
      }
      return [];
    },
  },
};
</script>

<style scoped>
h1 {
  color: #a09c94;
  font-family: sans-serif;
}
.v-data-table-header th {
  white-space: nowrap;
}
</style>
